import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ReactQuill from "react-quill";
import Delta from "quill-delta";
import "react-quill/dist/quill.snow.css";
import Navbar from "../../components/universal/Navbar";
import SearchBar from "../../components/universal/SearchBar";
import { toast } from "react-toastify";
import {
  readAgreementsRequest,
  saveAgreementRequest,
  updateAgreementRequest,
} from "../../store/Agreements/agreementsActions";

const formatAgreementTitle = (value) => String(value || "").trim();

const slugifyAgreementType = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const ELEMENT_NODE = 1;
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
  clipboard: {
    matchers: [
      [
        ELEMENT_NODE,
        (node, delta) => {
          const ops = (delta?.ops || []).map((op) => {
            if (!op?.insert) return op;
            const attributes = { ...(op.attributes || {}) };
            delete attributes.color;
            delete attributes.background;
            attributes.color = "#ffffff";
            return { ...op, attributes };
          });
          return new Delta(ops);
        },
      ],
    ],
  },
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "link",
];

const Agreements = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    typeofentity: "1",
    agreementType: "",
    agreementTitle: "",
    agreementText: "",
    isActive: "true",
  });

  useEffect(() => {
    const prefill = location?.state?.agreement;
    if (prefill) {
      setEditing(prefill);
      setForm({
        typeofentity: prefill?.typeofentity || "1",
        agreementType: prefill?.agreementType || "",
        agreementTitle: formatAgreementTitle(prefill?.agreementTitle || ""),
        agreementText: prefill?.agreementText || "",
        isActive: prefill?.isActive || "true",
      });
    }
  }, [location?.state]);

  const resetForm = () => {
    setEditing(null);
    setForm({
      typeofentity: form.typeofentity || "1",
      agreementType: "",
      agreementTitle: "",
      agreementText: "",
      isActive: "true",
    });
  };

  const isQuillEmpty = (value) => {
    const div = document.createElement("div");
    div.innerHTML = value || "";
    return !div.textContent?.trim();
  };

  const handleSave = () => {
    if (!form.agreementTitle?.trim()) {
      toast.error("Agreement title is required");
      return;
    }
    if (!form.agreementText || isQuillEmpty(form.agreementText)) {
      toast.error("Agreement text is required");
      return;
    }

    const payload = {
      ...form,
      agreementTitle: formatAgreementTitle(form.agreementTitle),
      agreementType:
        form.agreementType?.trim() ||
        slugifyAgreementType(form.agreementTitle),
      ...(editing?._id ? { _id: editing._id } : {}),
    };

    const action = editing ? updateAgreementRequest : saveAgreementRequest;

    dispatch(
      action(payload, (res) => {
        if (res?.type === 1) {
          toast.success(editing ? "Agreement updated" : "Agreement saved");
          resetForm();
          dispatch(readAgreementsRequest({ typeofentity: form.typeofentity }, () => {}));
          navigate("/agreements/all");
        } else {
          toast.error(res?.error || "Operation failed");
        }
      })
    );
  };

  return (
    <div className="App bg-[#191919] h-fit min-h-screen flex p-4">
      <Navbar />
      <div className="flex-1 md:pl-32 md:pt-0 pt-20">
        <div className="flex flex-col gap-4 h-full">
          <SearchBar placeholder="Agreements" value={""} onChangeSearchBar={() => {}} />

          <div className="MAIN-CONTENT flex-1 flex flex-col h-full gap-4">
            <div className="flex justify-between items-center">
              <p className="text-white text-3xl font-medium">
                {editing ? "Edit Agreement" : "Add Agreement"}
              </p>
              <div className="flex gap-4">
                <Link to="/agreements">
                  <button className="text-[13px] text-greyish border px-4 py-2 border-greyish bg-blackish rounded-xl">
                    Back to Analytics
                  </button>
                </Link>
                <Link to="/agreements/all">
                  <button className="text-[13px] text-greyish border px-4 py-2 border-greyish bg-blackish rounded-xl">
                    View All Agreements
                  </button>
                </Link>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="md:col-span-2 bg-blackish rounded-xl p-4 border border-greyish border-opacity-30">
                <p className="text-white text-sm font-semibold mb-3">
                  {editing ? "Update Agreement" : "Create Agreement"}
                </p>
                <label className="text-[12px] text-greyish font-medium">Entity Type</label>
                <select
                  value={form.typeofentity}
                  onChange={(e) => setForm((prev) => ({ ...prev, typeofentity: e.target.value }))}
                  className="bg-blackish p-2 w-full text-white text-[14px] mt-2 rounded-md h-10"
                >
                  <option value="1">Church</option>
                  <option value="2">Channel</option>
                  <option value="3">Ministry</option>
                </select>

                <label className="text-[12px] text-greyish font-medium mt-4 block">
                  Agreement Title
                </label>
                <input
                  value={form.agreementTitle}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      agreementTitle: e.target.value,
                    }))
                  }
                  placeholder="Enter agreement title"
                  className="bg-blackish p-2 w-full text-white text-[14px] mt-2 rounded-md h-10"
                />

                <label className="text-[12px] text-greyish font-medium mt-4 block">
                  Agreement Text
                </label>
                <div className="mt-2 rounded-md overflow-hidden border border-[#2a2a2a]">
                  <ReactQuill
                    theme="snow"
                    value={form.agreementText}
                    onChange={(value) => setForm((prev) => ({ ...prev, agreementText: value }))}
                    modules={quillModules}
                    formats={quillFormats}
                    className="agreement-quill"
                  />
                </div>

                <label className="text-[12px] text-greyish font-medium mt-4 block">
                  Active
                </label>
                <select
                  value={form.isActive}
                  onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.value }))}
                  className="bg-blackish p-2 w-full text-white text-[14px] mt-2 rounded-md h-10"
                >
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>

                <div className="flex gap-3 mt-4">
                  <button
                    className="text-yellowish bg-blackish font-medium text-[12px] border border-yellowish rounded-xl py-2 px-6"
                    onClick={handleSave}
                  >
                    {editing ? "Update" : "Save"}
                  </button>
                  {editing && (
                    <button
                      className="text-greyish bg-blackishV2 font-medium text-[12px] rounded-xl py-2 px-6"
                      onClick={resetForm}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Agreements;
